import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";

import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Skeleton,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import TextEditor from "app/Components/Fields/TextEditor";

import EditIconBtn from "app/Components/Buttons/IconBtns/EditIconBtn";
import SaveIconBtn from "app/Components/Buttons/IconBtns/SaveIconBtn";
import CancelIconBtn from "app/Components/Buttons/IconBtns/CancelIconBtn";

import { db } from "config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import UploadImage from "./UploadImage";
import UserInfo from "./UserInfo.jsx";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const BioContainer = styled(Box)(({ theme }) => ({
  minHeight: '200px',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  '& .ql-editor': {
    minHeight: '150px',
  },
}));

interface UserData {
  joinDate: string;
  profileBio: string;
  PreviewPic: string | null;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
}

interface EditProfileProps {
  // Add any props if needed
}

function EditProfile(props: EditProfileProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const [userData, setUserData] = useState<UserData>({
    joinDate: "",
    profileBio: "",
    PreviewPic: null,
  });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [bio, setBio] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    getUserData();
  }, [user.uid]);

  const getUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userDataRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userDataRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserData;
        setUserData(data);
        setBio(data.profileBio || "");
      } else {
        setError("User profile not found. Please try refreshing the page.");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load profile data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveBio = async () => {
    setError(null);
    setSuccess(null);
    
    if (bio.length > 1200) {
      setError("Please limit your bio to 1200 characters or less.");
      return;
    }
    
    try {
      await updateDoc(doc(db, "users", user.uid), {
        profileBio: bio,
      });
      
      setUserData(prev => ({ ...prev, profileBio: bio }));
      setEdit(false);
      setSuccess("Profile bio updated successfully!");
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error saving bio:", err);
      setError("Failed to save bio. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setEdit(false);
    setBio(userData.profileBio || "");
    setError(null);
  };

  const renderBioActions = () => {
    if (edit) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SaveIconBtn action={saveBio} />
          <CancelIconBtn action={handleCancelEdit} />
        </Stack>
      );
    }
    return (
      <Stack direction="row" justifyContent="flex-end">
        <EditIconBtn action={() => setEdit(true)} />
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Profile
        </Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Stack spacing={2}>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={200} />
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        Profile
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent>
              <UploadImage />
            </CardContent>
          </StyledCard>
        </Grid2>
        
        <Grid2 size={{ xs: 12, md: 8 }}>
          <StyledCard>
            <CardContent>
              <UserInfo userId={user.uid} />
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="primary" />
                    Bio
                  </Typography>
                  {!edit && (
                    <EditIconBtn action={() => setEdit(true)} />
                  )}
                </Stack>
                
                <Divider />
                
                <BioContainer>
                  {!edit ? (
                    <Box
                      sx={{
                        minHeight: '150px',
                        '& p': { margin: 0 },
                        '& p:not(:last-child)': { marginBottom: 1 },
                        color: bio ? 'text.primary' : 'text.secondary',
                        fontStyle: bio ? 'normal' : 'italic',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: bio || "No bio added yet. Click edit to add your bio.",
                      }}
                    />
                  ) : (
                    <TextEditor
                      text={bio}
                      handleChange={setBio}
                      placeholder="Write something about yourself..."
                    />
                  )}
                </BioContainer>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {bio.length}/1200 characters
                  </Typography>
                  {renderBioActions()}
                </Box>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default EditProfile;
